export class HttpError extends Error {
    status;
    body;
    constructor(message, status, body) {
        super(message);
        this.status = status;
        this.body = body;
        this.name = 'HttpError';
    }
}
let refreshInFlight = null;
async function parseBody(res) {
    if (res.status === 204 || res.status === 304)
        return null;
    const text = await res.text();
    if (!text)
        return null;
    try {
        return JSON.parse(text);
    }
    catch {
        return text;
    }
}
async function doRefresh(auth) {
    if (refreshInFlight)
        return refreshInFlight;
    refreshInFlight = (async () => {
        const refreshToken = await auth.tokenProvider.getRefreshToken();
        if (!refreshToken)
            return null;
        try {
            const res = await fetch(`${auth.baseUrl}/auth/refresh`, {
                method: 'POST',
                headers: {
                    accept: 'application/json',
                    'content-type': 'application/json',
                    ...(auth.refreshHeaders ?? {}),
                },
                body: JSON.stringify({ refreshToken }),
            });
            const parsed = await parseBody(res);
            if (!res.ok) {
                await auth.tokenProvider.clearTokens();
                return null;
            }
            const accessToken = parsed?.session?.accessToken;
            const newRefresh = parsed?.session?.refreshToken;
            if (!accessToken || !newRefresh) {
                await auth.tokenProvider.clearTokens();
                return null;
            }
            await auth.tokenProvider.setTokens({
                accessToken,
                refreshToken: newRefresh,
            });
            return accessToken;
        }
        finally {
            refreshInFlight = null;
        }
    })();
    return refreshInFlight;
}
export async function httpJson(url, init = {}) {
    const { json, auth, skipAuth, ...fetchInit } = init;
    const headers = new Headers(fetchInit.headers ?? {});
    headers.set('accept', 'application/json');
    let body = fetchInit.body;
    if (json !== undefined) {
        headers.set('content-type', 'application/json');
        body = JSON.stringify(json);
    }
    if (auth && !skipAuth) {
        const token = auth.tokenProvider.getAccessToken();
        if (token) {
            headers.set('authorization', `Bearer ${token}`);
        }
    }
    const firstRes = await fetch(url, {
        ...fetchInit,
        headers,
        body,
    });
    if (firstRes.status === 401 && auth && !skipAuth) {
        const newAccess = await doRefresh(auth);
        if (newAccess) {
            headers.set('authorization', `Bearer ${newAccess}`);
            const retryRes = await fetch(url, {
                ...fetchInit,
                headers,
                body,
            });
            const retryBody = await parseBody(retryRes);
            if (!retryRes.ok) {
                throw new HttpError(`HTTP ${retryRes.status}`, retryRes.status, retryBody);
            }
            return retryBody;
        }
    }
    const parsed = await parseBody(firstRes);
    if (!firstRes.ok) {
        throw new HttpError(`HTTP ${firstRes.status}`, firstRes.status, parsed);
    }
    return parsed;
}
/**
 * Same auth/refresh behavior as httpJson, but sends multipart/form-data (FormData).
 * IMPORTANT: do NOT set content-type header manually; the browser will add the boundary.
 */
export async function httpFormData(url, formData, init = {}) {
    const { auth, skipAuth, ...fetchInit } = init;
    const headers = new Headers(fetchInit.headers ?? {});
    headers.set('accept', 'application/json');
    if (auth && !skipAuth) {
        const token = auth.tokenProvider.getAccessToken();
        if (token) {
            headers.set('authorization', `Bearer ${token}`);
        }
    }
    const firstRes = await fetch(url, {
        ...fetchInit,
        headers,
        body: formData,
    });
    if (firstRes.status === 401 && auth && !skipAuth) {
        const newAccess = await doRefresh(auth);
        if (newAccess) {
            headers.set('authorization', `Bearer ${newAccess}`);
            const retryRes = await fetch(url, {
                ...fetchInit,
                headers,
                body: formData,
            });
            const retryBody = await parseBody(retryRes);
            if (!retryRes.ok) {
                throw new HttpError(`HTTP ${retryRes.status}`, retryRes.status, retryBody);
            }
            return retryBody;
        }
    }
    const parsed = await parseBody(firstRes);
    if (!firstRes.ok) {
        throw new HttpError(`HTTP ${firstRes.status}`, firstRes.status, parsed);
    }
    return parsed;
}
